import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Download, KeyRound, Trash2, Eye, FileSpreadsheet, UserMinus,
} from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post, del, download } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import {
  Card, Button, Badge, Table, Select, SearchInput, SectionTitle, Modal,
  ConfirmModal, Pagination, EmptyState, Loader, PersonCell,
} from '../../components/UI';
import { fmtDateTime } from '../../utils/format';

const PER_PAGE = 50;

const STATUS_TONE = { created: 'success', updated: 'info', skipped: 'warning', pending: 'neutral' };
const STATUS_WORD = { created: 'Added', updated: 'Updated', skipped: 'Not imported', pending: 'Still to import' };

/**
 * The uploaded sheet, whole.
 *
 * The import only writes the columns it was told to; this screen shows the
 * file as it arrived — every column, every row — with the person each row
 * became beside it. The operations are the ones the Users tab has: search,
 * export, login details, taking people off the event and a quick view.
 */
export default function EventSheet() {
  const { id } = useParams();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const base = `/${pathname.split('/').filter(Boolean)[0] || 'admin'}`;
  const toast = useToast();
  const { isViewer } = useAuth();

  const [sheetId, setSheetId] = useState(params.get('sheet') || '');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState('');
  const [confirmDrop, setConfirmDrop] = useState(false);
  const [confirmSheet, setConfirmSheet] = useState(false);

  const { data: event } = useFetch(`/events/${id}`);
  const { data: sheets, loading: listing, reload: reloadList } = useFetch('/users/import/sheets', { eventId: id });

  // Newest upload unless a link named one.
  useEffect(() => {
    if (sheetId || !sheets?.length) return;
    setSheetId(sheets[0]._id);
  }, [sheets, sheetId]);

  const query = useMemo(() => ({
    page, limit: PER_PAGE, ...(search.trim() ? { search: search.trim() } : {}), ...(status ? { status } : {}),
  }), [page, search, status]);

  const { data: sheet, meta, loading, reload } = useFetch(
    sheetId ? `/users/import/sheets/${sheetId}` : null, query, { skip: !sheetId, withMeta: true },
  );

  const rows = sheet?.rows || [];
  const headers = sheet?.headers || [];
  const mapped = useMemo(() => {
    // Column index → the field it was imported into, for the column heading.
    const out = {};
    for (const [key, i] of Object.entries(sheet?.mapping || {})) out[i] = key;
    for (const [label, i] of Object.entries(sheet?.extras || {})) out[i] = `extra: ${label}`;
    return out;
  }, [sheet]);

  const pickable = rows.filter((r) => r.user);
  const allPicked = pickable.length > 0 && pickable.every((r) => picked.has(String(r.user)));

  const toggle = (userId) => {
    const next = new Set(picked);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    setPicked(next);
  };
  const togglePage = () => {
    const next = new Set(picked);
    if (allPicked) pickable.forEach((r) => next.delete(String(r.user)));
    else pickable.forEach((r) => next.add(String(r.user)));
    setPicked(next);
  };

  const chooseSheet = (next) => {
    setSheetId(next);
    setPage(1);
    setPicked(new Set());
    setParams(next ? { sheet: next } : {});
  };

  const exportSheet = async () => {
    try { await download(`/users/import/sheets/${sheetId}/export`, {}, `${(sheet?.fileName || 'uploaded-sheet').replace(/\.[^.]+$/, '')}-full.xlsx`); }
    catch (err) { toast.error(err.message); }
  };

  /** Login details, in batches of 25 like the Users tab. */
  const sendCredentials = async () => {
    const ids = [...picked];
    if (!ids.length) return;
    setBusy('creds');
    let sent = 0; const failed = [];
    for (let i = 0; i < ids.length; i += 25) {
      try {
        const res = await post('/users/credentials/send', { users: ids.slice(i, i + 25), event: id });
        sent += res.data?.sent || 0;
        failed.push(...(res.data?.failed || []));
      } catch (err) {
        failed.push({ name: `${Math.min(25, ids.length - i)} people`, reason: err.message });
      }
    }
    setBusy('');
    toast[failed.length ? 'error' : 'success'](failed.length
      ? `Sent to ${sent}; ${failed.length} could not be sent`
      : `Login details sent to ${sent} ${sent === 1 ? 'person' : 'people'}`);
  };

  /** Takes the selected people off this event. Their accounts stay. */
  const dropFromEvent = async () => {
    const ids = [...picked];
    setBusy('drop');
    let done = 0; const failed = [];
    for (const userId of ids) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await post(`/users/${userId}/events`, { eventIds: [id], action: 'remove' });
        done += 1;
      } catch (err) { failed.push(err.message); }
    }
    setBusy('');
    setConfirmDrop(false);
    setPicked(new Set());
    reload();
    toast[failed.length ? 'error' : 'success'](failed.length
      ? `${done} taken off the event; ${failed.length} failed`
      : `${done} ${done === 1 ? 'person' : 'people'} taken off this event`);
  };

  const removeSheet = async () => {
    setBusy('sheet');
    try {
      await del(`/users/import/sheets/${sheetId}`);
      toast.success('The uploaded sheet has been removed');
      setConfirmSheet(false);
      chooseSheet('');
      reloadList();
    } catch (err) { toast.error(err.message); } finally { setBusy(''); }
  };

  if (listing) return <Loader label="Opening the sheet" full />;

  if (!sheets?.length) {
    return (
      <div>
        <Button variant="subtle" size="sm" icon={ArrowLeft} onClick={() => nav(`${base}/events/${id}`)} style={{ marginBottom: 14 }}>
          Back to the event
        </Button>
        <EmptyState
          icon={FileSpreadsheet}
          message="No sheet has been uploaded to this event yet"
          hint="Import a participant list from the Users tab and the file is kept here in full — every column, even the ones nothing was mapped to."
          action={<Button size="sm" onClick={() => nav(`${base}/events/${id}`)}>Go to Users</Button>}
        />
      </div>
    );
  }

  return (
    <div>
      <Button variant="subtle" size="sm" icon={ArrowLeft} onClick={() => nav(`${base}/events/${id}`)} style={{ marginBottom: 14 }}>
        Back to {event?.title || 'the event'}
      </Button>

      <SectionTitle
        title="Full sheet"
        subtitle={sheet
          ? `${sheet.fileName || 'Uploaded sheet'} — ${headers.length} columns, ${sheet.counts?.stored ?? 0} rows as uploaded`
          : 'The file exactly as it was uploaded'}
      />

      {/* ── what was uploaded, and what it did ─────────────────────── */}
      <Card style={{ marginBottom: 16 }} padding={16}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          {sheets.length > 1 && (
            <Select value={sheetId} onChange={(e) => chooseSheet(e.target.value)} style={{ maxWidth: 330, fontSize: 12.5 }}>
              {sheets.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.fileName || 'Sheet'} — {fmtDateTime(s.createdAt)}
                </option>
              ))}
            </Select>
          )}
          {sheet && (
            <>
              <Badge tone="success">{sheet.counts?.created || 0} added</Badge>
              <Badge tone="info">{sheet.counts?.updated || 0} updated</Badge>
              <Badge tone={sheet.counts?.skipped ? 'warning' : 'neutral'}>{sheet.counts?.skipped || 0} not imported</Badge>
              <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>
                {sheet.sheetName ? `"${sheet.sheetName}", headers on row ${sheet.headerRow}. ` : ''}
                Uploaded {fmtDateTime(sheet.createdAt)}{sheet.uploadedBy?.name ? ` by ${sheet.uploadedBy.name}` : ''}.
              </span>
              {sheet.truncated && <Badge tone="warning">Only the first 5,000 rows were kept</Badge>}
            </>
          )}
        </div>
      </Card>

      {/* ── operations ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 11, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search every column of the sheet…"
          style={{ flex: 1, minWidth: 240, maxWidth: 380 }}
        />
        <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ maxWidth: 190, fontSize: 12.5 }}>
          <option value="">All rows</option>
          <option value="imported">Imported only</option>
          <option value="created">Added</option>
          <option value="updated">Updated</option>
          <option value="skipped">Not imported</option>
          <option value="pending">Still to import</option>
        </Select>
        <Button variant="outline" size="sm" icon={Download} onClick={exportSheet}>Excel</Button>
        {picked.size > 0 && !isViewer && (
          <>
            <Button size="sm" icon={KeyRound} loading={busy === 'creds'} onClick={sendCredentials}>
              Send login details to {picked.size}
            </Button>
            <Button variant="danger" size="sm" icon={UserMinus} onClick={() => setConfirmDrop(true)}>
              Remove {picked.size} from this event
            </Button>
          </>
        )}
        {!isViewer && (
          <Button variant="subtle" size="sm" icon={Trash2} onClick={() => setConfirmSheet(true)}>
            Delete this upload
          </Button>
        )}
      </div>

      <Table
        loading={loading}
        rows={rows}
        keyField="line"
        empty={search || status ? 'No row in the sheet matches that' : 'This upload has no rows'}
        columns={[
          {
            key: '_pick',
            title: (
              <input
                type="checkbox" checked={allPicked} onChange={togglePage} title="Select the rows on this page"
                style={{ accentColor: 'var(--gold-400)' }} disabled={!pickable.length}
              />
            ),
            render: (r) => (r.user ? (
              <input
                type="checkbox" checked={picked.has(String(r.user))} onChange={() => toggle(String(r.user))}
                style={{ accentColor: 'var(--gold-400)' }}
              />
            ) : null),
          },
          { key: '_line', title: 'Row', render: (r) => <span style={{ color: 'var(--text-lo)', fontVariantNumeric: 'tabular-nums' }}>{r.line}</span> },
          {
            key: '_person',
            title: 'Imported as',
            render: (r) => (r.person ? (
              <div style={{ minWidth: 170 }}>
                <PersonCell person={r.person} />
                <Badge tone={STATUS_TONE[r.status]} style={{ marginTop: 4 }}>{STATUS_WORD[r.status]}</Badge>
              </div>
            ) : (
              <div style={{ maxWidth: 200 }}>
                <Badge tone="warning">{STATUS_WORD[r.status] || 'Not imported'}</Badge>
                {r.reason && <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 4 }}>{r.reason}</p>}
              </div>
            )),
          },
          ...headers.map((h, i) => ({
            key: `c${i}`,
            title: (
              <span title={h} style={{ display: 'inline-block', maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>
                {h}
                {mapped[i] && <span style={{ color: 'var(--gold-400)' }}> •</span>}
              </span>
            ),
            render: (r) => {
              const v = r.cells?.[i] ?? '';
              if (!v) return <span style={{ color: 'var(--ink-700)' }}>—</span>;
              return (
                <span title={v} style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                  {v}
                </span>
              );
            },
          })),
          {
            key: '_view',
            title: '',
            align: 'right',
            render: (r) => <Button size="sm" variant="subtle" icon={Eye} onClick={() => setView(r)}>View</Button>,
          },
        ]}
      />

      <Pagination meta={meta} onChange={setPage} />

      <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 14, lineHeight: 1.7, maxWidth: 760 }}>
        A <span style={{ color: 'var(--gold-400)' }}>•</span> next to a column heading means that column was matched to a
        field (or kept as an extra) when the sheet was imported; the rest are here for reference only. Tick rows to send
        those people their login details or take them off this event — their accounts and anything else they are on stay
        as they are. <strong>Delete this upload</strong> removes only the stored copy of the file.
      </p>

      {/* ── one row, every column ──────────────────────────────────── */}
      <Modal
        open={Boolean(view)} onClose={() => setView(null)} width={620}
        title={view?.person?.name || `Row ${view?.line || ''}`}
        subtitle={view ? `Row ${view.line} of ${sheet?.fileName || 'the sheet'} — ${STATUS_WORD[view.status] || ''}` : undefined}
        footer={<Button variant="subtle" onClick={() => setView(null)}>Close</Button>}
      >
        {view && (
          <>
            {view.reason && (
              <p style={{ fontSize: 12.5, color: 'var(--warn)', marginBottom: 12 }}>{view.reason}</p>
            )}
            {view.person && (
              <div style={{ padding: 12, marginBottom: 14, background: 'var(--ink-900)', border: '1px solid var(--border)', borderRadius: 'var(--r)' }}>
                <PersonCell person={view.person} />
                <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 6 }}>
                  {[view.person.mobile, view.person.email, view.person.employeeCode && `#${view.person.employeeCode}`, view.person.officeLocation]
                    .filter(Boolean).join(' · ') || 'No contact details on record'}
                </p>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {headers.map((h, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '8px 10px', background: i % 2 ? 'transparent' : 'var(--ink-900)', borderRadius: 6 }}>
                  <p style={{ fontSize: 12, color: 'var(--text-lo)' }}>
                    {h}
                    {mapped[i] && <span style={{ color: 'var(--gold-400)' }}> •</span>}
                  </p>
                  <p style={{ fontSize: 12.5, wordBreak: 'break-word' }}>{view.cells?.[i] || '—'}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>

      <ConfirmModal
        open={confirmDrop} onClose={() => setConfirmDrop(false)} onConfirm={dropFromEvent} loading={busy === 'drop'} danger
        title="Take these people off the event?" confirmLabel="Remove from event"
        message={`${picked.size} ${picked.size === 1 ? 'person' : 'people'} will no longer be on this event. Their accounts, and any other event they are on, are untouched. They are not notified.`}
      />
      <ConfirmModal
        open={confirmSheet} onClose={() => setConfirmSheet(false)} onConfirm={removeSheet} loading={busy === 'sheet'} danger
        title="Delete this upload?" confirmLabel="Delete the upload"
        message="The stored copy of the file is deleted. The people it imported stay on the event — remove them from the Users tab if the upload was wrong."
      />
    </div>
  );
}
